/**
 * Home module UI state — year/month/day navigation, view mode, FAB, editing.
 */

import { create } from 'zustand'
import { startOfWeek, addWeeks, subWeeks } from 'date-fns'
import type { MonthlyHabitItem } from '@turnifly/shared/types'

type ViewMode = 'monthly' | 'weekly'

interface HomeState {
  year: number
  month: number // 1-indexed
  viewMode: ViewMode
  isFabOpen: boolean
  weekStart: string // ISO date string for start of current week (Monday)
  isViewModeOpen: boolean
  editingHabit: MonthlyHabitItem | null
  viewingHabitId: string | null
  addHabitGoalId: string | null

  nextMonth: () => void
  prevMonth: () => void
  nextWeek: () => void
  prevWeek: () => void
  setViewMode: (mode: ViewMode) => void
  setFabOpen: (open: boolean) => void
  setViewModeOpen: (open: boolean) => void
  goToToday: () => void
  setEditingHabit: (habit: MonthlyHabitItem | null) => void
  setViewingHabitId: (id: string | null) => void
  setAddHabitGoalId: (id: string | null) => void
}

/** Local date to YYYY-MM-DD (NOT UTC — avoids timezone shift) */
const toIso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const mondayOf = (d: Date): string => toIso(startOfWeek(d, { weekStartsOn: 1 }))

export const useHomeStore = create<HomeState>()((set) => ({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  viewMode: 'monthly',
  isFabOpen: false,
  weekStart: mondayOf(new Date()),
  isViewModeOpen: false,
  editingHabit: null,
  viewingHabitId: null,
  addHabitGoalId: null,

  nextMonth: () =>
    set((s) => {
      if (s.month === 12) return { year: s.year + 1, month: 1 }
      return { month: s.month + 1 }
    }),

  prevMonth: () =>
    set((s) => {
      if (s.month === 1) return { year: s.year - 1, month: 12 }
      return { month: s.month - 1 }
    }),

  nextWeek: () =>
    set((s) => {
      const next = addWeeks(new Date(s.weekStart), 1)
      return { weekStart: toIso(next), year: next.getFullYear(), month: next.getMonth() + 1 }
    }),

  prevWeek: () =>
    set((s) => {
      const prev = subWeeks(new Date(s.weekStart), 1)
      return { weekStart: toIso(prev), year: prev.getFullYear(), month: prev.getMonth() + 1 }
    }),

  setViewMode: (viewMode) => set({
    viewMode,
    isViewModeOpen: false,
    // Weekly ga o'tganda hozirgi haftaga qaytarish
    ...(viewMode === 'weekly' ? { weekStart: mondayOf(new Date()) } : {}),
  }),
  setFabOpen: (isFabOpen) => set({ isFabOpen }),
  setViewModeOpen: (isViewModeOpen) => set({ isViewModeOpen }),
  goToToday: () =>
    set({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      weekStart: mondayOf(new Date()),
    }),
  setEditingHabit: (editingHabit) => set({ editingHabit, isFabOpen: !!editingHabit }),
  setViewingHabitId: (viewingHabitId) => set({ viewingHabitId }),
  setAddHabitGoalId: (addHabitGoalId) => set({ addHabitGoalId }),
}))

export type { ViewMode }
